import { ApiClientError, apiClient } from '@/lib/api-client';
import type {
  ApiUser,
  AuthErrorCode,
  LoginCredentials,
  LoginResponse,
  MeResponse,
} from '@/types/auth';
import { UserType } from '@/types/auth';

// Custom auth error with typed error codes
export class AuthError extends Error {
  constructor(
    message: string,
    public code: AuthErrorCode
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

// Return type for login/getCurrentUser (unwrapped user)
export interface AuthResult {
  user: ApiUser;
  message: string;
}

export const authService = {
  /**
   * Login with email and password
   * Only allows ADMIN users
   */
  async login(credentials: LoginCredentials): Promise<AuthResult> {
    try {
      const response = await apiClient.post<LoginResponse>('/auth/login', credentials);

      // Extract user from wrapped response
      const user = response.result.user;

      // Validate user is ADMIN
      if (user.type !== UserType.ADMIN) {
        // Logout immediately to clear cookies
        await this.logout().catch(() => {});
        throw new AuthError('Access denied. Admin privileges required.', 'NOT_ADMIN');
      }

      return { user, message: response.message };
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      if (error instanceof ApiClientError) {
        if (error.statusCode === 401) {
          throw new AuthError('Invalid email or password', 'INVALID_CREDENTIALS');
        }
        throw new AuthError(error.message, 'UNKNOWN');
      }
      throw new AuthError('Network error. Please try again.', 'NETWORK_ERROR');
    }
  },

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<AuthResult> {
    try {
      const response = await apiClient.get<MeResponse>('/auth/me');

      // Extract user from wrapped response
      const user = response.result.user;

      // Validate user is ADMIN
      if (user.type !== UserType.ADMIN) {
        throw new AuthError('Access denied. Admin privileges required.', 'NOT_ADMIN');
      }

      return { user, message: response.message };
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      if (error instanceof ApiClientError) {
        if (error.statusCode === 401) {
          throw new AuthError('Session expired', 'SESSION_EXPIRED');
        }
        throw new AuthError(error.message, 'UNKNOWN');
      }
      throw new AuthError('Network error. Please try again.', 'NETWORK_ERROR');
    }
  },

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout');
    } finally {
      // Always clear local state even if API call fails
      apiClient.clearCsrfToken();
    }
  },
};
